import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { Registration } from '../../../types/registration';

interface StayHistoryEntry {
  year: string;
  accommodation: string;
}

interface StayHistoryCardProps {
  registration: Registration;
}

const StayHistoryCard: React.FC<StayHistoryCardProps> = ({ registration }) => {
  const [stayHistory, setStayHistory] = useState<StayHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStayHistory = async () => {
      if (!registration?.registrationInfo?.email) {
        setLoading(false);
        return;
      }

      try {
        // Query primaryCampers collection by email
        const primaryCampersQuery = query(
          collection(db, 'primaryCampers'),
          where('email', '==', registration.registrationInfo.email)
        );
        const primaryCampersSnapshot = await getDocs(primaryCampersQuery);

        if (!primaryCampersSnapshot.empty) {
          const primaryCamperData = primaryCampersSnapshot.docs[0].data();
          let history: StayHistoryEntry[] = [];

          // Check if new array format exists
          if (primaryCamperData.accommodationHistory && Array.isArray(primaryCamperData.accommodationHistory)) {
            // Use new array format
            history = primaryCamperData.accommodationHistory.map((entry: any) => ({
              year: String(entry.year),
              accommodation: String(entry.placeCode)
            }));
          } else {
            // Fall back to old format (Acc.1-Acc.8 and Acc01-Acc08)
            for (let i = 1; i <= 8; i++) {
              const yearField = `Acc.${i}`;
              const accommodationField = `Acc${i.toString().padStart(2, '0')}`; // Acc01, Acc02, etc.
              
              const year = primaryCamperData[yearField];
              const accommodation = primaryCamperData[accommodationField];

              // Only add if both year and accommodation exist
              if (year && accommodation) {
                history.push({
                  year: String(year),
                  accommodation: String(accommodation)
                });
              }
            }

            // Sort by year descending (most recent first)
            history.sort((a, b) => parseInt(b.year) - parseInt(a.year));
          }

          setStayHistory(history);
        }
      } catch (error) {
        console.error('Error fetching stay history:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStayHistory();
  }, [registration]);

  return (
    <div className="card mb-3">
      <div className="card-body p-3">
        <h6 className="card-title mb-3" style={{ fontWeight: 'bold', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '0.5rem' }}>
          Stay History
        </h6>

        {loading ? (
          <p className="text-muted">Loading stay history...</p>
        ) : stayHistory.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>Year</th>
                  <th>Accommodation</th>
                </tr>
              </thead>
              <tbody>
                {stayHistory.map((entry, index) => (
                  <tr key={index}>
                    <td>{entry.year}</td>
                    <td>
                      {entry.accommodation === 'No History' ? (
                        <span style={{ color: '#6c757d', fontStyle: 'italic' }}>
                          {entry.accommodation}
                        </span>
                      ) : (
                        <Link 
                          to={`/admin/inventory?placeCode=${encodeURIComponent(entry.accommodation)}&select=true`}
                          style={{ 
                            color: '#007bff', 
                            textDecoration: 'none',
                            fontWeight: 500
                          }}
                          onMouseOver={(e) => e.currentTarget.style.textDecoration = 'underline'}
                          onMouseOut={(e) => e.currentTarget.style.textDecoration = 'none'}
                        >
                          {entry.accommodation}
                        </Link>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-muted">No stay history available</p>
        )}
      </div>
    </div>
  );
};

export default StayHistoryCard;

